"use client";

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";

import { Button } from "@/ui/components/ui/button";
import {
	type PeptideProfile,
	type StackCombination,
	type GoalStack,
	type BlendMatch,
	MAX_STACK_SIZE,
	resolveGoalSlug,
} from "@/config/peptide-stacks";
import { PEPTIDE_LINKS } from "@/config/peptide-links";
import { getAllPairwiseSynergies, getAggregateScore, detectBlendMatch } from "@/lib/stack-builder/synergy";

import posthog from "posthog-js";
import { toast } from "sonner";

import { useAddCartLines } from "@/ui/components/cart/use-add-to-cart";

import { PeptideCard } from "./_components/peptide-card";
import { StackAnalysis } from "./_components/stack-analysis";
import { GoalSelector } from "./_components/goal-selector";
import { GoalStackCards } from "./_components/goal-stack-cards";
import { StackFaq } from "./_components/stack-faq";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import type { InternalSource } from "@/lib/analytics/internal-source";
import {
	clampStackBuilderQuantity,
	getStackBuilderDefaultVariant,
	isStackBuilderVariantPurchasable,
} from "@/lib/stack-builder/purchasable-selection";
import { createBrowserUuid } from "@/lib/browser-uuid";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StackBuilderClientProps {
	products: unknown[];
	profiles: PeptideProfile[];
	combinations: StackCombination[];
	goalStacks: GoalStack[];
	blendMatches: BlendMatch[];
	channel: string;
	locale: string;
	currency: string;
}

type BuilderMode = "guided" | "free";

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

function readInitialSlugs(searchParams: URLSearchParams): string[] {
	const raw = searchParams.get("s");
	if (!raw) return [];
	return raw
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);
}

function readInitialMode(searchParams: URLSearchParams): BuilderMode {
	const raw = searchParams.get("mode");
	return raw === "guided" ? "guided" : "free";
}

function syncUrl(slugs: string[], mode: BuilderMode, goal: string | null) {
	const url = new URL(window.location.href);
	if (slugs.length > 0) {
		url.searchParams.set("s", slugs.join(","));
	} else {
		url.searchParams.delete("s");
	}
	if (mode !== "free") {
		url.searchParams.set("mode", mode);
	} else {
		url.searchParams.delete("mode");
	}
	if (goal) {
		url.searchParams.set("goal", goal);
	} else {
		url.searchParams.delete("goal");
	}
	window.history.replaceState(null, "", url.toString());
}

// ---------------------------------------------------------------------------
// Default variant helper
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Find product by slug
// ---------------------------------------------------------------------------

function findProductBySlug(products: unknown[], slug: string): unknown | null {
	// Stack Builder profile slugs (e.g. "tb500") don't always equal the Saleor product slug
	// (e.g. "tb-500"). Resolve via PEPTIDE_LINKS, falling back to the input for blend slugs
	// or anything not in the link map.
	const productSlug = PEPTIDE_LINKS[slug]?.productSlug ?? slug;
	return (
		(products as Array<Record<string, unknown>>).find((p) => {
			return p.slug === productSlug;
		}) ?? null
	);
}

// ---------------------------------------------------------------------------
// Price extraction helper
// ---------------------------------------------------------------------------

function getVariantPrice(product: unknown, variantId: string): number {
	const p = product as Record<string, unknown> | null;
	if (!p) return 0;
	const variants = p.variants as Array<Record<string, unknown>> | undefined;
	if (!variants) return 0;

	const variant = variants.find((v) => (v.id as string) === variantId);
	if (!variant) return 0;

	// Saleor pricing structure: variant.pricing.price.gross.amount
	const pricing = variant.pricing as Record<string, unknown> | undefined;
	if (!pricing) return 0;
	const price = pricing.price as Record<string, unknown> | undefined;
	if (!price) return 0;
	const gross = price.gross as Record<string, unknown> | undefined;
	if (!gross) return 0;
	return (gross.amount as number) ?? 0;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StackBuilderClient({
	products,
	profiles,
	combinations: _combinations,
	goalStacks,
	blendMatches: _blendMatches,
	channel,
	locale,
	currency,
}: StackBuilderClientProps) {
	const t = useTranslations("stackBuilder");
	const tCommon = useTranslations("common");
	const searchParams = useSearchParams();
	const addCartLines = useAddCartLines();
	const sourceParam = searchParams.get("src");
	const entrySource: InternalSource | null =
		sourceParam === "calculator" || sourceParam === "quiz" || sourceParam === "stack-builder"
			? sourceParam
			: null;
	const entrySourceDetail = searchParams.get("src_slug");
	const requestedInitialSlugs = readInitialSlugs(searchParams);
	const resolveInitialSlugs = () =>
		requestedInitialSlugs
			.filter((slug) => profiles.some((profile) => profile.slug === slug))
			.filter((slug) => getStackBuilderDefaultVariant(findProductBySlug(products, slug)) !== null)
			.slice(0, MAX_STACK_SIZE);

	// ---- State ----
	const [selectedSlugs, setSelectedSlugs] = useState<string[]>(resolveInitialSlugs);
	const [unavailableInitialSlugs] = useState<string[]>(() => {
		const accepted = new Set(resolveInitialSlugs());
		return requestedInitialSlugs.filter((slug) => !accepted.has(slug));
	});

	const [mode, setMode] = useState<BuilderMode>(() => readInitialMode(searchParams));

	const [variantChoices, setVariantChoices] = useState<
		Record<string, { variantId: string; quantity: number }>
	>(() => {
		const choices: Record<string, { variantId: string; quantity: number }> = {};
		for (const slug of resolveInitialSlugs()) {
			const product = findProductBySlug(products, slug);
			const defaultVar = getStackBuilderDefaultVariant(product);
			if (defaultVar) {
				choices[slug] = defaultVar;
			}
		}
		return choices;
	});

	const [showAnalysis, setShowAnalysis] = useState(false);
	const stackAddLockRef = useRef(false);

	// Canonicalise the incoming `?goal=` exactly once, against the same allow-list
	// generateMetadata uses. Everything downstream (syncUrl, the goal selector's active state,
	// ShareButtons -> /api/og/stack, PostHog) reads this value, so an unknown or camelCase goal
	// can neither reach the OG headline nor silently deselect the matching goal card.
	const [activeGoal, setActiveGoal] = useState<string | null>(() =>
		resolveGoalSlug(searchParams.get("goal")),
	);
	const enteredRef = useRef(false);
	useEffect(() => {
		if (enteredRef.current) return;
		enteredRef.current = true;
		posthog.capture("stack_builder_entered", {
			mode,
			goal: activeGoal,
			selected_slugs: selectedSlugs,
			unavailable_slugs: unavailableInitialSlugs,
			internal_source: entrySource,
			internal_source_detail: entrySourceDetail,
			channel,
		});
	}, [activeGoal, channel, entrySource, entrySourceDetail, mode, selectedSlugs, unavailableInitialSlugs]);

	// ---- URL sync ----
	useEffect(() => {
		syncUrl(selectedSlugs, mode, activeGoal);
	}, [selectedSlugs, mode, activeGoal]);

	// ---- Computed values ----
	const synergies = useMemo(() => getAllPairwiseSynergies(selectedSlugs), [selectedSlugs]);

	const aggregateScore = useMemo(() => getAggregateScore(selectedSlugs), [selectedSlugs]);

	const blendMatch = useMemo(() => detectBlendMatch(selectedSlugs), [selectedSlugs]);

	const blendProduct = useMemo(() => {
		if (!blendMatch) return null;
		return findProductBySlug(products, blendMatch.blend);
	}, [blendMatch, products]);

	const selectedProducts = useMemo(() => {
		return selectedSlugs.map((slug) => {
			const product = findProductBySlug(products, slug);
			const profile = profiles.find((p) => p.slug === slug);
			const variant = variantChoices[slug] ?? null;
			return { slug, product, profile, variant };
		});
	}, [selectedSlugs, products, profiles, variantChoices]);

	const totalPrice = useMemo(() => {
		return selectedProducts.reduce((sum, { product, variant }) => {
			if (!product || !variant) return sum;
			const price = getVariantPrice(product, variant.variantId);
			return sum + price * variant.quantity;
		}, 0);
	}, [selectedProducts]);

	// ---- Handlers ----
	const handleToggle = useCallback(
		(slug: string) => {
			const isCurrentlySelected = selectedSlugs.includes(slug);
			const product = findProductBySlug(products, slug);
			const defaultVariant = isCurrentlySelected ? null : getStackBuilderDefaultVariant(product);
			if (!isCurrentlySelected && !defaultVariant) return;
			if (isCurrentlySelected) {
				posthog.capture("stack_builder_peptide_removed", {
					slug,
					stack_size: selectedSlugs.length - 1,
				});
			} else if (selectedSlugs.length < MAX_STACK_SIZE) {
				posthog.capture("stack_builder_peptide_added", {
					slug,
					stack_size: selectedSlugs.length + 1,
				});
			}

			setSelectedSlugs((prev) => {
				if (prev.includes(slug)) {
					return prev.filter((s) => s !== slug);
				}
				if (prev.length >= MAX_STACK_SIZE) {
					return prev;
				}
				return [...prev, slug];
			});

			setVariantChoices((prev) => {
				if (prev[slug]) return prev;
				if (!defaultVariant) return prev;
				return { ...prev, [slug]: defaultVariant };
			});
		},
		[products, selectedSlugs],
	);

	const handleRemove = useCallback(
		(slug: string) => {
			posthog.capture("stack_builder_peptide_removed", {
				slug,
				stack_size: selectedSlugs.length - 1,
			});
			setSelectedSlugs((prev) => prev.filter((s) => s !== slug));
			setVariantChoices((prev) => {
				const next = { ...prev };
				delete next[slug];
				return next;
			});
		},
		[selectedSlugs],
	);

	const handleVariantChange = useCallback(
		(slug: string, variantId: string) => {
			const product = findProductBySlug(products, slug) as Record<string, unknown> | null;
			const variants = Array.isArray(product?.variants)
				? (product.variants as Array<Record<string, unknown>>)
				: [];
			const candidate = variants.find((variant) => variant.id === variantId);
			if (!isStackBuilderVariantPurchasable(candidate)) return;
			setVariantChoices((prev) => ({
				...prev,
				[slug]: { variantId, quantity: prev[slug]?.quantity ?? 1 },
			}));
		},
		[products],
	);

	const handleQuantityChange = useCallback((slug: string, quantity: number) => {
		setVariantChoices((prev) => {
			const existing = prev[slug];
			if (!existing) return prev;
			return { ...prev, [slug]: { ...existing, quantity: clampStackBuilderQuantity(quantity) } };
		});
	}, []);

	const handleLoadGoalStack = useCallback(
		(peptides: string[]) => {
			const choices: Record<string, { variantId: string; quantity: number }> = {};
			for (const slug of peptides.filter((candidate) => profiles.some((p) => p.slug === candidate))) {
				const product = findProductBySlug(products, slug);
				const defaultVar = getStackBuilderDefaultVariant(product);
				if (defaultVar) {
					choices[slug] = defaultVar;
				}
				if (Object.keys(choices).length >= MAX_STACK_SIZE) break;
			}
			setSelectedSlugs(Object.keys(choices));
			setMode("guided");
			setVariantChoices(choices);
		},
		[products, profiles],
	);

	const handleModeChange = useCallback((newMode: BuilderMode) => {
		setMode(newMode);
	}, []);

	const handleAddToCart = useCallback(async (): Promise<boolean> => {
		if (stackAddLockRef.current) return false;
		const itemsToAdd = selectedProducts.filter(({ variant }) => variant?.variantId);
		if (itemsToAdd.length === 0) return false;
		stackAddLockRef.current = true;
		const cartAddAttemptId = createBrowserUuid();
		const internalSource = entrySource ?? "stack-builder";
		const internalSourceDetail = entrySourceDetail ?? activeGoal ?? undefined;
		const lines = itemsToAdd.flatMap(({ variant, product, profile }) => {
			if (!variant?.variantId) return [];
			const productRecord = product as Record<string, unknown> | null;
			const productVariants = Array.isArray(productRecord?.variants)
				? (productRecord.variants as Array<Record<string, unknown>>)
				: [];
			const selectedVariant = productVariants.find((item) => item.id === variant.variantId);
			if (!isStackBuilderVariantPurchasable(selectedVariant)) return [];
			const productSlug = typeof productRecord?.slug === "string" ? productRecord.slug : undefined;
			const productName =
				typeof productRecord?.name === "string" ? productRecord.name : (profile?.name ?? productSlug ?? "");
			return [
				{
					channel,
					variantId: variant.variantId,
					quantity: variant.quantity,
					productSlug,
					productName,
					variantName: typeof selectedVariant?.name === "string" ? selectedVariant.name : undefined,
					internalSource,
					internalSourceDetail,
					addSurface: "stack_builder" as const,
				},
			];
		});
		if (lines.length !== itemsToAdd.length) {
			stackAddLockRef.current = false;
			toast.error(t("addToCartFailed"));
			return false;
		}

		// Shared runner: one batch mutation (partial retries cannot duplicate lines),
		// transient drawer rows for every item, own toast copy kept.
		const allOk = await addCartLines(lines, {
			cartAddAttemptId,
			suppressToast: true,
			onSuccess: () => {
				posthog.capture("stack_builder_add_to_cart", {
					slugs: selectedSlugs,
					total_price: totalPrice,
					currency,
					internal_source: internalSource,
					// Unchanged property shape: `null` when neither entry detail nor goal is set.
					internal_source_detail: entrySourceDetail ?? activeGoal,
					add_surface: "stack_builder",
					cart_add_attempt_id: cartAddAttemptId,
					goal: activeGoal,
				});
			},
		});
		stackAddLockRef.current = false;

		if (!allOk) toast.error(t("addToCartFailed"));

		return allOk;
	}, [
		addCartLines,
		selectedProducts,
		selectedSlugs,
		totalPrice,
		currency,
		channel,
		t,
		entrySource,
		entrySourceDetail,
		activeGoal,
	]);

	// ---- Mobile sticky-bar add-to-cart (direct, no overlay) ----
	const [mobileAddState, setMobileAddState] = useState<"idle" | "adding" | "added" | "failed">("idle");

	const handleMobileAddToCart = useCallback(async () => {
		setMobileAddState("adding");
		let ok = false;
		try {
			ok = await handleAddToCart();
		} catch {
			ok = false;
		}
		setMobileAddState(ok ? "added" : "failed");
		setTimeout(() => setMobileAddState("idle"), ok ? 1500 : 3000);
	}, [handleAddToCart]);

	// ---- Format price ----
	const formattedTotal = useMemo(() => {
		return new Intl.NumberFormat(locale, {
			style: "currency",
			currency,
		})
			.format(totalPrice)
			.replace(/\u202F/g, "\u00A0");
	}, [totalPrice, locale, currency]);

	const totalResolveItems = useMemo(
		() => (totalPrice > 0 ? [{ id: "total", amount: totalPrice, currency }] : []),
		[totalPrice, currency],
	);
	const totalPresMap = usePricingResolvePresentations(locale, channel, totalResolveItems);
	const totalPresentation = totalPresMap.get("total") ?? null;

	const prevAnalyzedKey = useRef<string>("");
	useEffect(() => {
		if (selectedSlugs.length >= 2 && aggregateScore !== null) {
			const key = [...selectedSlugs].sort().join(",");
			if (key !== prevAnalyzedKey.current) {
				prevAnalyzedKey.current = key;
				posthog.capture("stack_builder_result_viewed", {
					slugs: selectedSlugs,
					synergy_score: aggregateScore,
					peptide_count: selectedSlugs.length,
				});
			}
		}
	}, [selectedSlugs, aggregateScore]);

	// ---- Render ----
	return (
		<div className="relative">
			{unavailableInitialSlugs.length > 0 && (
				<div
					className="border-warning/30 bg-warning/10 mb-4 rounded-lg border p-3 text-sm text-foreground"
					role="status"
				>
					{unavailableInitialSlugs.map((slug) => {
						const name = profiles.find((profile) => profile.slug === slug)?.name ?? slug;
						return <p key={slug}>{t("unavailableAria", { name })}</p>;
					})}
				</div>
			)}
			{/* Mode toggle */}
			<div className="mb-6 flex gap-2">
				<Button
					variant={mode === "guided" ? "default" : "outline-solid"}
					size="sm"
					onClick={() => handleModeChange("guided")}
				>
					{t("guidedMode")}
				</Button>
				<Button
					variant={mode === "free" ? "default" : "outline-solid"}
					size="sm"
					onClick={() => handleModeChange("free")}
				>
					{t("freeMode")}
				</Button>
			</div>

			{/* Desktop two-column layout */}
			<div className="flex flex-col gap-6 lg:flex-row">
				{/* Left column: Selection grid + goal stacks + FAQ */}
				<div className="w-full lg:w-3/5">
					{/* Guided mode: Goal selector */}
					{mode === "guided" && (
						<div className="mb-6">
							<GoalSelector goalStacks={goalStacks} activeGoal={activeGoal} onSelectGoal={setActiveGoal} />
						</div>
					)}

					{/* Goal stack cards (visible in guided mode when a goal is selected) */}
					{mode === "guided" && activeGoal && (
						<div className="mb-6">
							<GoalStackCards
								goalStacks={goalStacks}
								activeGoal={activeGoal}
								selectedSlugs={selectedSlugs}
								onLoadStack={handleLoadGoalStack}
							/>
						</div>
					)}

					{/* Peptide selection grid */}
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						{profiles.map((profile) => {
							const product = findProductBySlug(products, profile.slug);
							const isSelected = selectedSlugs.includes(profile.slug);
							const isDisabled =
								!isSelected &&
								(selectedSlugs.length >= MAX_STACK_SIZE || getStackBuilderDefaultVariant(product) === null);

							return (
								<PeptideCard
									key={profile.slug}
									profile={profile}
									product={product}
									isSelected={isSelected}
									isDisabled={isDisabled}
									variantChoice={variantChoices[profile.slug] ?? null}
									onToggle={handleToggle}
									onVariantChange={handleVariantChange}
									onQuantityChange={handleQuantityChange}
									onRemove={handleRemove}
									currency={currency}
									locale={locale}
									channel={channel}
								/>
							);
						})}
					</div>

					{/* FAQ section */}
					<div className="mt-8">
						<StackFaq />
					</div>
				</div>

				{/* Right column: Stack analysis (desktop) */}
				<div className="hidden w-full lg:block lg:w-2/5">
					<div className="sticky top-24">
						<StackAnalysis
							selectedProducts={selectedProducts}
							synergies={synergies}
							aggregateScore={aggregateScore}
							blendMatch={blendMatch}
							blendProduct={blendProduct}
							totalPrice={totalPrice}
							formattedTotal={formattedTotal}
							totalPresentation={totalPresentation}
							currency={currency}
							locale={locale}
							channel={channel}
							goal={activeGoal}
							onRemove={handleRemove}
							onVariantChange={handleVariantChange}
							onQuantityChange={handleQuantityChange}
							onAddToCart={handleAddToCart}
						/>
					</div>
				</div>
			</div>

			{/* Mobile: Sticky bottom bar */}
			{selectedSlugs.length > 0 && (
				<div className="fixed inset-x-0 bottom-0 z-40 flex flex-col gap-2 border-t border-border bg-card p-3 shadow-lg lg:hidden">
					{/* Summary + Details toggle (opens the full analysis overlay) */}
					<button
						type="button"
						className="flex w-full items-center justify-between"
						onClick={() => setShowAnalysis(true)}
					>
						<span className="text-sm font-medium">
							{selectedSlugs.length} {t("analysis.peptides", { count: selectedSlugs.length })} &middot;{" "}
							{totalPresentation ? (
								<ReferencePrimaryPrice
									presentation={totalPresentation}
									locale={locale}
									channel={channel}
									size="sm"
									className="text-sm font-medium"
									showReferenceTooltip={false}
								/>
							) : (
								formattedTotal
							)}
						</span>
						<span className="text-sm font-semibold text-primary">{t("analysis.viewStack")} &uarr;</span>
					</button>

					{/* Direct add-to-cart — no need to open the overlay first */}
					<Button
						variant={mobileAddState === "failed" ? "destructive" : "default"}
						size="default"
						className="w-full"
						disabled={mobileAddState === "adding"}
						onClick={() => void handleMobileAddToCart()}
						aria-live="polite"
					>
						{mobileAddState === "adding" ? (
							<span className="flex items-center gap-2">
								<span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
								{t("adding")}
							</span>
						) : mobileAddState === "failed" ? (
							t("addToCartFailed")
						) : mobileAddState === "added" ? (
							t("added")
						) : (
							t("addToCart")
						)}
					</Button>
				</div>
			)}

			{/* Mobile: Full-screen analysis overlay */}
			{showAnalysis && (
				<div className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden">
					<div className="flex items-center justify-between border-b border-border p-4">
						<h2 className="text-lg font-semibold">{t("analysis.yourStack")}</h2>
						<button
							type="button"
							onClick={() => setShowAnalysis(false)}
							className="rounded-md p-1 hover:bg-muted"
							aria-label={tCommon("close")}
						>
							<X className="h-5 w-5" />
						</button>
					</div>
					<div className="flex-1 overflow-y-auto p-4">
						<StackAnalysis
							selectedProducts={selectedProducts}
							synergies={synergies}
							aggregateScore={aggregateScore}
							blendMatch={blendMatch}
							blendProduct={blendProduct}
							totalPrice={totalPrice}
							formattedTotal={formattedTotal}
							totalPresentation={totalPresentation}
							currency={currency}
							locale={locale}
							channel={channel}
							goal={activeGoal}
							onRemove={handleRemove}
							onVariantChange={handleVariantChange}
							onQuantityChange={handleQuantityChange}
							onAddToCart={handleAddToCart}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
